import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CheckmarkCircle12Filled,
    Warning12Filled,
    Circle12Regular,
    Dismiss12Regular,
    MoreHorizontal16Regular,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody } from '../../../utils/askLensJournal';
import {
    canResolveSwotEvidence,
    filterSupportedInsightsActions,
    isInsightsReadOnlyBlocked,
    resolveSwotEvidenceTarget,
} from '../../../utils/intelligenceInsightsActions';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';
import drawerStyles from './intelligenceInsightsHighlights.module.css';

const TAG_CLASS = {
    green: styles.swotTagGreen,
    amber: styles.swotTagAmber,
    blue: styles.swotTagBlue,
    red: styles.swotTagRed,
};

const HEAD_CLASS = {
    green: styles.swotQuadHeadGreen,
    amber: styles.swotQuadHeadAmber,
    blue: styles.swotQuadHeadBlue,
    red: styles.swotQuadHeadRed,
};

const SCOPE_LABELS = {
    team: 'Team',
    strategy_roadmap: 'Strategy & Roadmap',
    technology_architecture: 'Technology & Architecture',
    scale_resilience: 'Scale & Resilience',
    service_management_devops: 'Service Management & DevOps',
    compliance_cybersecurity: 'Compliance & Cybersecurity',
    business_systems_it: 'Business Systems & IT',
    it_budget: 'IT Budget',
};

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

function scopeLabel(area) {
    return SCOPE_LABELS[area] || titleCase(area);
}

function itemPrefill(item) {
    return [
        item?.title,
        item?.body,
        item?.detail?.whyItMatters,
        item?.detail?.strategicImplication,
    ]
        .filter(Boolean)
        .join('\n\n');
}

function EvidenceBadge({ level }) {
    const normalized = String(level || 'moderate').toLowerCase();
    if (normalized === 'strong') {
        return (
            <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceStrong}`}>
                <CheckmarkCircle12Filled style={{ color: '#22c55e' }} aria-hidden />
                Strong evidence
            </span>
        );
    }
    if (normalized === 'limited') {
        return (
            <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceLimited}`}>
                <Circle12Regular style={{ color: '#989bad' }} aria-hidden />
                Limited evidence
            </span>
        );
    }
    return (
        <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceModerate}`}>
            <Warning12Filled style={{ color: '#f59e0b' }} aria-hidden />
            Moderate evidence
        </span>
    );
}

function DetailDrawer({ open, item, onClose, onAction }) {
    if (!open || !item) return null;
    const detail = item.detail || {};
    const fullAssessment = detail.fullAssessment || item.body || '';
    const whyItMatters = detail.whyItMatters || '';
    const scopeImplication = detail.strategicImplication || '';
    const limitations = detail.assessmentLimitations || [];
    const recommended = detail.recommendedResponse || '';
    const scopeAreas = detail.scopeAreas || [];
    const eyebrow =
        scopeAreas.length > 0
            ? scopeAreas.map(scopeLabel).join(' · ')
            : item.quadrantLabel || 'SWOT';
    const evidenceCount =
        detail.sourcesReviewed ||
        (item.evidenceReferenceIds || []).length ||
        (item.relatedFindingIds || []).length +
            (item.relatedRiskIds || []).length +
            (item.relatedEvidenceMatterIds || []).length;
    const viewEvidenceAction =
        (item.actions || []).find(
            a => a.key === 'view_evidence' || a.key === 'open_evidence_panel',
        ) || { key: 'view_evidence', label: 'View evidence' };
    const canViewEvidence = canResolveSwotEvidence(item, viewEvidenceAction);
    const journalAction =
        (item.actions || []).find(a => a.key === 'save_to_journal') || {
            key: 'save_to_journal',
            label: 'Save to Journal',
        };
    const otherActions = filterSupportedInsightsActions(
        (item.actions || []).filter(
            a =>
                a.key &&
                ![
                    'view_details',
                    'view_evidence',
                    'open_evidence_panel',
                    'save_to_journal',
                    'open_risks_signals',
                    'open_key_findings',
                    'open_evidence_follow_ups',
                ].includes(a.key),
        ),
        { moduleKey: 'swot_analysis', context: { item } },
    );

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480 }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <p className={styles.swotDetailEyebrow}>{eyebrow}</p>
                            <h2 className={styles.swotDetailTitle}>{item.title}</h2>
                        </div>
                        <button
                            type="button"
                            className={drawerStyles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.swotDetailBody}>
                        {fullAssessment ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Full Assessment</p>
                                <p className={styles.swotDetailLead}>{fullAssessment}</p>
                            </section>
                        ) : null}

                        {whyItMatters ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Why It Matters</p>
                                <p className={styles.swotDetailText}>{whyItMatters}</p>
                            </section>
                        ) : null}

                        {scopeImplication ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Scope Implication</p>
                                <p className={styles.swotDetailText}>{scopeImplication}</p>
                            </section>
                        ) : null}

                        {limitations.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Assessment Limitations</p>
                                <p className={styles.swotDetailLimitation}>
                                    {limitations.join(' ')}
                                </p>
                            </section>
                        ) : null}

                        {recommended ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Recommended Response</p>
                                <div className={styles.swotDetailResponse}>{recommended}</div>
                            </section>
                        ) : null}

                        {evidenceCount > 0 || canViewEvidence ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Evidence</p>
                                <div className={styles.swotDetailEvidenceRow}>
                                    {evidenceCount > 0 ? (
                                        <span className={styles.swotDetailEvidenceCount}>
                                            {evidenceCount} evidence item
                                            {evidenceCount === 1 ? '' : 's'}
                                        </span>
                                    ) : (
                                        <span className={styles.swotDetailEvidenceCount}>
                                            Evidence available
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.swotDetailEvidenceLink}
                                        disabled={!canViewEvidence}
                                        aria-disabled={!canViewEvidence}
                                        onClick={() => {
                                            if (!canViewEvidence) return;
                                            onAction?.(viewEvidenceAction, item);
                                        }}>
                                        View evidence
                                    </button>
                                </div>
                            </section>
                        ) : null}

                        <div className={styles.swotDetailActions}>
                            <button
                                type="button"
                                className={styles.swotDetailGhostBtn}
                                onClick={() => onAction?.(journalAction, item)}>
                                Save to Journal
                            </button>
                            {otherActions.map(action => (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={styles.swotDetailGhostBtn}
                                    onClick={() => onAction?.(action, item)}>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

const IntelligenceInsightsSwot = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const [materialityFilter, setMaterialityFilter] = useState('all');
    const [scopeFilter, setScopeFilter] = useState('all');
    const [confidenceFilter, setConfidenceFilter] = useState('all');
    const [detailItem, setDetailItem] = useState(null);
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    const filteredQuadrants = useMemo(() => {
        const quads = data?.quadrants || [];
        return quads.map(quad => {
            let items = quad.items || [];
            if (materialityFilter !== 'all') {
                items = items.filter(item =>
                    String(item.materiality || '')
                        .toLowerCase()
                        .includes(materialityFilter.toLowerCase()),
                );
            }
            if (confidenceFilter !== 'all') {
                items = items.filter(
                    item =>
                        item.detail?.confidence &&
                        String(item.detail.confidence).toLowerCase() ===
                            confidenceFilter.toLowerCase(),
                );
            }
            if (scopeFilter !== 'all') {
                const needle = scopeFilter.toLowerCase().replace(/\s+/g, '_');
                const labels = data?.filters?.scopeAreaLabels || {};
                items = items.filter(item => {
                    const scopes = item.detail?.scopeAreas || [];
                    if (!scopes.length) return false;
                    return scopes.some(scope => {
                        const key = String(scope).toLowerCase();
                        const label = String(labels[scope] || titleCase(scope)).toLowerCase();
                        return (
                            key === needle ||
                            label === scopeFilter.toLowerCase() ||
                            key.includes(needle)
                        );
                    });
                });
            }
            return { ...quad, filteredItems: items, filteredCount: items.length };
        });
    }, [data?.quadrants, materialityFilter, scopeFilter, confidenceFilter]);

    const handleAction = async (action, item = null) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const target = action.target || {};
        const text = itemPrefill(item);

        if (key === 'view_details') {
            setEvidenceTarget(null);
            setDetailItem(item);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailItem(null);
            const resolved = resolveSwotEvidenceTarget(item, action);
            if (!resolved) return;
            setEvidenceTarget({
                mode: 'object',
                objectType: resolved.objectType,
                objectId: resolved.objectId,
                contextLabel: item?.quadrantLabel || titleCase(item?.quadrantKey || ''),
                evidenceLevel: item?.evidence || null,
                itemTitle: item?.title || null,
            });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(text);
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(text);
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(text);
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'SWOT Analysis',
                    headline: item?.title || '',
                    bodyText: buildInsightsItemJournalBody(item),
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
        if (key === 'open_risks_signals') {
            onOpenModule?.('risks_signals');
            return;
        }
        if (key === 'open_key_findings') {
            onOpenModule?.('key_findings');
            return;
        }
        if (key === 'open_evidence_follow_ups') {
            onOpenModule?.('evidence_follow_ups');
            return;
        }
    };

    if (error || !data || data.isEmpty) {
        const failed =
            Boolean(error) ||
            data?.status === 'failed' ||
            data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'SWOT Analysis'}
                subtitle={
                    data?.subtitle ||
                    'Evidence-backed strategic strengths, weaknesses, opportunities and threats.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="swot"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    const materialityOptions = data.filters?.options?.materiality || [
        'all',
        'material',
        'notable',
    ];
    const scopeOptions = data.filters?.options?.scope_area || ['all'];
    const scopeLabels = data.filters?.scopeAreaLabels || {};
    const confidenceOptions = data.filters?.options?.confidence || [
        'all',
        'high',
        'medium',
        'low',
    ];

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                {data.warnings?.length ? (
                    <div className={styles.footerBanner} style={{ marginBottom: 16 }}>
                        <div>
                            <p className={styles.footerBannerTitle}>Complete with warnings</p>
                            <p className={styles.footerBannerMeta}>
                                {data.warnings[0]?.message ||
                                    'SWOT used a deterministic scaffold from canonical analysis.'}
                            </p>
                        </div>
                    </div>
                ) : null}

                <div className={styles.swotFilters}>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Materiality:</span>
                        <select
                            className={styles.filterSelect}
                            value={materialityFilter}
                            onChange={e => setMaterialityFilter(e.target.value)}
                            aria-label="Materiality filter">
                            {materialityOptions.map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all' ? 'All' : titleCase(opt)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Scope area:</span>
                        <select
                            className={styles.filterSelect}
                            value={scopeFilter}
                            onChange={e => setScopeFilter(e.target.value)}
                            aria-label="Scope area filter">
                            {scopeOptions.map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all'
                                        ? 'All'
                                        : scopeLabels[opt] || titleCase(String(opt).replace(/_/g, ' '))}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <span className={styles.filterLabel}>Confidence:</span>
                        <select
                            className={styles.filterSelect}
                            value={confidenceFilter}
                            onChange={e => setConfidenceFilter(e.target.value)}
                            aria-label="Confidence filter">
                            {confidenceOptions.map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === 'all' ? 'All' : titleCase(opt)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.swotGrid}>
                    {filteredQuadrants.map(quad => (
                        <section key={quad.key} className={styles.swotQuad}>
                            <div
                                className={`${styles.swotQuadHead} ${HEAD_CLASS[quad.tone] || ''}`}>
                                <span className={`${styles.swotTag} ${TAG_CLASS[quad.tone]}`}>
                                    {quad.label}
                                </span>
                                <p className={styles.swotDef}>{quad.definition}</p>
                                <span className={styles.swotCount}>{quad.filteredCount}</span>
                            </div>

                            <div className={styles.swotCardList}>
                                {quad.filteredItems.length ? (
                                    quad.filteredItems.map(item => {
                                        const cardActions = filterSupportedInsightsActions(
                                            item.actions || [],
                                            {
                                                moduleKey: 'swot_analysis',
                                                context: { item },
                                            },
                                        ).filter(a => a.key !== 'view_details');
                                        const linkActions = cardActions.filter(a =>
                                            [
                                                'view_evidence',
                                                'open_evidence_panel',
                                                'open_key_findings',
                                                'open_risks_signals',
                                                'open_evidence_follow_ups',
                                            ].includes(a.key),
                                        );
                                        const solidActions = cardActions.filter(
                                            a => !linkActions.includes(a),
                                        );
                                        const materiality = String(item.materiality || '').toLowerCase();
                                        const isMaterial = materiality.includes('material');
                                        const detailAction = {
                                            key: 'view_details',
                                            label: 'View details',
                                        };

                                        return (
                                            <article key={item.id} className={styles.swotCard}>
                                                <div className={styles.swotCardTop}>
                                                    <div className={styles.swotCardMeta}>
                                                        <span
                                                            className={`${styles.swotMaterialChip}${
                                                                isMaterial
                                                                    ? ` ${styles.swotMaterialChipMaterial}`
                                                                    : ''
                                                            }`}>
                                                            {String(item.materiality || '').toUpperCase()}
                                                        </span>
                                                        <EvidenceBadge level={item.evidence} />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={styles.swotCardKebab}
                                                        aria-label="More actions"
                                                        onClick={() =>
                                                            handleAction(detailAction, item)
                                                        }>
                                                        <MoreHorizontal16Regular />
                                                    </button>
                                                </div>
                                                <p className={styles.swotCardTitle}>{item.title}</p>
                                                <p className={styles.swotCardBody}>{item.body}</p>
                                                <div className={styles.swotCardActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.swotCardLink}
                                                        onClick={() =>
                                                            handleAction(detailAction, item)
                                                        }>
                                                        View details
                                                    </button>
                                                    {linkActions.map(action => (
                                                        <button
                                                            key={action.key}
                                                            type="button"
                                                            className={styles.swotCardLink}
                                                            onClick={() =>
                                                                handleAction(action, item)
                                                            }>
                                                            {action.label}
                                                        </button>
                                                    ))}
                                                    {solidActions.map(action => (
                                                        <button
                                                            key={action.key}
                                                            type="button"
                                                            className={styles.swotCardBtn}
                                                            onClick={() =>
                                                                handleAction(action, item)
                                                            }>
                                                            {action.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className={styles.swotEmptyQuad}>
                                        {materialityFilter === 'all' &&
                                        scopeFilter === 'all' &&
                                        confidenceFilter === 'all'
                                            ? `No evidence-backed material ${quad.label.toLowerCase()} were identified in this analysis version.`
                                            : `No ${quad.label.toLowerCase()} in the current filter set.`}
                                    </div>
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            </InsightsModuleChrome>

            <DetailDrawer
                open={Boolean(detailItem)}
                item={detailItem}
                onClose={() => setDetailItem(null)}
                onAction={handleAction}
            />

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || null}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                variant="swot"
                contextLabel={evidenceTarget?.contextLabel || null}
                evidenceLevel={evidenceTarget?.evidenceLevel || null}
                itemTitle={evidenceTarget?.itemTitle || null}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        title: panelCard?.headline || detailItem?.title || evidenceTarget?.itemTitle,
                        body: panelCard?.conclusion || detailItem?.body,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsSwot;
